import { createElement } from "react";
import { RouteMovedNotice } from "@/components/RouteMovedNotice";
import { Navigate } from "react-router-dom";
import {
  IconDashboard,
  IconKey,
  IconClipboardList,
  IconShield,
} from "@tabler/icons-react";
import { lazyNamed, type PortalDef } from "@livingsyncs/ui";
// Previously imported from "./app.portal" (frontend) — resolved (Phase 6
// Task 6.2/6.3): profileRoute + the settings page components moved to
// @livingsyncs/ui after Phase 5's real-caller audit confirmed profileRoute
// genuinely shared (admin.portal.ts and this file both import it), and this
// follow-up confirmed the individual settings components too. Imported via
// the deep path, not the main barrel — see @livingsyncs/ui's index.ts for
// why (sharedRoutes.ts's own dependency chain would otherwise ship in every
// consuming app's bundle unconditionally).
import {
  profileRoute,
  SettingsPage,
  SettingsIndexRedirect,
  OrganizationSettingsPage,
  FacilitySettingsPage,
} from "@livingsyncs/ui/src/router/sharedRoutes";

// ── Lazy pages (each its own code-split chunk — US-044 AC11) ─────────────────
const AuditorLayout = lazyNamed(
  () => import("@/components/AuditorLayout"),
  "AuditorLayout",
);
const AuditorDashboard = lazyNamed(
  () => import("@/pages/audit/AuditorDashboard"),
  "AuditorDashboard",
);
// GroupHomesList and MyRequestsPage are no longer routed directly — they're
// embedded (statically imported) inside AuditAccessPage, which owns the one
// `/audit/access` chunk for both tabs (same pattern as AuditorsPage /
// AuditorGroupContent / InvitationRequestsContent below).
const AuditAccessPage = lazyNamed(
  () => import("@/pages/audit/AuditAccessPage"),
  "AuditAccessPage",
);
const GroupHomeAuditPage = lazyNamed(
  () => import("@/pages/audit/GroupHomeAuditPage"),
  "GroupHomeAuditPage",
);
const AuditRequestForm = lazyNamed(
  () => import("@/pages/audit/AuditRequestForm"),
  "AuditRequestForm",
);
const FacilityAuditPage = lazyNamed(
  () => import("@/pages/audit/FacilityAuditPage"),
  "FacilityAuditPage",
);
const AuditSessionsList = lazyNamed(
  () => import("@/pages/audit/AuditSessionsList"),
  "AuditSessionsList",
);
const AuditSessionPage = lazyNamed(
  () => import("@/pages/audit/AuditSessionPage"),
  "AuditSessionPage",
);
const AuditResidentDetailPage = lazyNamed(
  () => import("@/pages/audit/ResidentDetailPage"),
  "ResidentDetailPage",
);
const AuditIncidentDetailPage = lazyNamed(
  () => import("@/pages/audit/IncidentDetailPage"),
  "IncidentDetailPage",
);
const AuditCarePlanDetailPage = lazyNamed(
  () => import("@/pages/audit/CarePlanDetailPage"),
  "CarePlanDetailPage",
);
const FacilityFindingsPage = lazyNamed(
  () => import("@/pages/audit/FacilityFindingsPage"),
  "FacilityFindingsPage",
);
const AuditHistoryPage = lazyNamed(
  () => import("@/pages/audit/AuditHistoryPage"),
  "AuditHistoryPage",
);
const EmergencyContactsAudit = lazyNamed(
  () => import("@/pages/audit/EmergencyContactsAudit"),
  "EmergencyContactsAudit",
);
const PhysicianCompletenessAudit = lazyNamed(
  () => import("@/pages/audit/PhysicianCompletenessAudit"),
  "PhysicianCompletenessAudit",
);
const VitalSignsCompliancePage = lazyNamed(
  () => import("@/pages/audit/VitalSignsCompliancePage"),
  "VitalSignsCompliancePage",
);
const AdlCompliancePage = lazyNamed(
  () => import("@/pages/audit/AdlCompliancePage"),
  "AdlCompliancePage",
);
const CareNotesAnalysisPage = lazyNamed(
  () => import("@/pages/audit/CareNotesAnalysisPage"),
  "CareNotesAnalysisPage",
);
const AppointmentsTrackingPage = lazyNamed(
  () => import("@/pages/audit/AppointmentsTrackingPage"),
  "AppointmentsTrackingPage",
);
const PharmacyInsuranceCompletenessPage = lazyNamed(
  () => import("@/pages/audit/PharmacyInsuranceCompletenessPage"),
  "PharmacyInsuranceCompletenessPage",
);
const InviteAuditors = lazyNamed(
  () => import("@/pages/audit/InviteAuditors"),
  "InviteAuditors",
);
const AuditorsPage = lazyNamed(
  () => import("@/pages/audit/AuditorsPage"),
  "AuditorsPage",
);

// Settings pages (SettingsPage/OrganizationSettingsPage/FacilitySettingsPage
// imported above, already lazy — see @livingsyncs/ui/src/router/sharedRoutes.ts).

/**
 * The Audit portal authored as data (US-044 AC6, audit pilot). Started as a
 * faithful 1:1 translation of the previous `/audit` JSX subtree with no
 * `access` metadata, because no audit route used a client-side grant/shift
 * delegate. F-audit-portal-ia added the first real permission gate on
 * `auditors` / `invite-auditors`. A prior revision of this gate used
 * `context.isAuditorGroupAdmin` directly (a role-flag delegate) because no
 * `PermissionKey` distinguished role:audit:admin from role:audit:external at
 * the time; that was a role hardcode wearing a permission's clothes,
 * evaluated in the same `passesAccess` codepath as real permission gates
 * (unlike the deliberately-excluded `shiftRequired`).
 *
 * F-auditor-direct-invite's first attempt at the root-cause fix granted
 * role:audit:admin `auditor-group:read`/`write` — but that key is what
 * several PLATFORM-only `/api/admin/auditor-groups*` routes gate on with no
 * portal restriction, so it let a group admin list every OTHER tenant's
 * auditor groups, create new groups, or invite a manager into a group they
 * don't own (filed as F-086, caught by
 * `backend/src/app/api/admin/auditor-groups/__tests__/permission-matrix.test.ts`
 * and `backend/src/lib/permissionRegistry.test.ts`'s frozen snapshot before
 * it shipped). The actual fix (F-086) is NARROWER: role:audit:admin holds
 * `auditor-invitation:read`/`write` instead (role:audit:external holds
 * neither) — a key no platform-only route gates on — and `auditors` /
 * `invite-auditors` below declare `access: { resource: "auditor-invitation"
 * }` (read) / `access: { anyOf: ["auditor-invitation:write"] }` (the FE has
 * no verb-override field by design, §11.3 — a write-gated route entry uses
 * `anyOf: ["<resource>:write"]`, same idiom as `employees →
 * anyOf:employee:write`). `auditor-group:*` stays platform-admin/manager-only.
 * The facility-scoped routes below are still ungated client-side; the
 * AUTHORITATIVE per-facility grant enforcement is server-side (`withAuth`'s
 * `auditorFacility` resolver on every /api/audit/* route); client-side, the
 * facility pages surface the backend's grant verdict themselves (e.g.
 * FacilityAuditPage renders Access Denied on the grant-check error). The
 * never-wired client-side twin (`AuditorFacilityRoute`/`grantRequired`) was
 * deleted rather than kept as a second implementation of the same rule.
 *
 * US-139 — that claim is now TRUE. When written it was not: the `auditorFacility`
 * resolver it names was used by ZERO routes. Enforcement was hand-rolled two
 * incompatible ways, one of them `canAccessFacility` — a facility MEMBERSHIP
 * check, structurally false for every auditor — which had silently killed 11
 * endpoints including session validate and complete. Path-facility audit routes
 * now declare `auditorFacility` + `auditorScopes` on the wrapper, which resolves
 * the grant, enforces its date window and `access_scopes`, and records the access.
 *
 * Three routes keep an in-body grant check, each documented at the call site:
 * `findings` POST and `sessions` POST take the facility from the request BODY (a
 * resolver runs before the handler, where the body can be read only once), and
 * `grants/check` reports whether a grant exists, so it must not require one.
 * `backend/src/lib/authz/__tests__/auditFacilityGate.test.ts` is the ratchet that
 * keeps `canAccessFacility` out of auditor gating from here on.
 */
export const auditPortal: PortalDef = {
  id: "audit",
  basePath: "/audit",
  layout: AuditorLayout,
  routes: [
    {
      index: true,
      Component: AuditorDashboard,
      nav: { label: "Dashboard", icon: IconDashboard, order: 1 },
    },
    // F-audit-access-consolidation: "Browse Facilities" (old `group-homes`
    // nav route, GroupHomesList) and "Group Home Access" (old `my-requests`
    // nav route, MyRequestsPage) folded into one tabbed `access` page — see
    // AuditAccessPage's doc comment for the reasoning. `group-homes` and
    // `my-requests` are kept as redirects (not a clean break): both were
    // `nav`-listed sidebar entries, so an auditor could plausibly have either
    // bookmarked. Neither route declared `access` before this change, so the
    // redirects don't either — same portal-membership-only gate as before.
    {
      path: "access",
      Component: AuditAccessPage,
      nav: { label: "Access", icon: IconKey, order: 2 },
    },
    {
      path: "group-homes",
      element: createElement(Navigate, {
        to: "/audit/access?tab=find-organization",
        replace: true,
      }),
    },
    { path: "group-homes/:groupHomeId", Component: GroupHomeAuditPage },
    {
      path: "my-requests",
      element: createElement(Navigate, {
        to: "/audit/access?tab=my-requests",
        replace: true,
      }),
    },
    { path: "requests/new", Component: AuditRequestForm },
    { path: "facilities/:facilityId", Component: FacilityAuditPage },

    // Audit Session & Report Generation
    {
      path: "sessions",
      Component: AuditSessionsList,
      nav: { label: "My Sessions", icon: IconClipboardList, order: 3 },
    },
    { path: "sessions/:sessionId", Component: AuditSessionPage },

    // Audit Annotation Detail Pages
    {
      path: "facilities/:facilityId/residents/:residentId",
      Component: AuditResidentDetailPage,
    },
    {
      path: "facilities/:facilityId/incidents/:incidentId",
      Component: AuditIncidentDetailPage,
    },
    {
      path: "facilities/:facilityId/care-plans/:planId",
      Component: AuditCarePlanDetailPage,
    },
    {
      path: "facilities/:facilityId/findings",
      Component: FacilityFindingsPage,
    },
    { path: "facilities/:facilityId/history", Component: AuditHistoryPage },
    {
      path: "facilities/:facilityId/emergency-contacts",
      Component: EmergencyContactsAudit,
    },
    {
      path: "facilities/:facilityId/physician-completeness",
      Component: PhysicianCompletenessAudit,
    },
    {
      path: "facilities/:facilityId/vital-signs-compliance",
      Component: VitalSignsCompliancePage,
    },
    {
      path: "facilities/:facilityId/adl-compliance",
      Component: AdlCompliancePage,
    },
    {
      path: "facilities/:facilityId/care-notes-analysis",
      Component: CareNotesAnalysisPage,
    },
    {
      path: "facilities/:facilityId/appointments-tracking",
      Component: AppointmentsTrackingPage,
    },
    {
      path: "facilities/:facilityId/pharmacy-insurance-completeness",
      Component: PharmacyInsuranceCompletenessPage,
    },

    // Auditor Group Admin routes (F-audit-portal-ia). The Auditors page owns
    // group overview + members roster + invitation requests + a link to
    // Invite Auditors — replacing the old Settings-tab-borrowed placeholders
    // (settings/auditor-group, settings/invitation-requests, removed below).
    // Both routes are role:audit:admin only; role:audit:external must not see
    // them (nav or direct URL).
    {
      path: "auditors",
      Component: AuditorsPage,
      nav: { label: "Auditors", icon: IconShield, order: 4 },
      access: { resource: "auditor-invitation" },
    },
    {
      path: "invite-auditors",
      Component: InviteAuditors,
      access: { anyOf: ["auditor-invitation:write"] },
    },

    // Personal account page, reached from the header account menu.
    profileRoute,

    // Settings (nested under /audit) — inline tabs render null; SettingsPage
    // handles their content. Mirrors the app settings subtree. Profile +
    // Notifications moved to the standalone /audit/profile page. Auditor
    // group / invitation requests moved off this shared shell onto the
    // dedicated `auditors` route above (F-audit-portal-ia). F-audit-access-
    // consolidation folded Browse Facilities + Group Home Access into one
    // `access` nav slot, freeing a slot back up — order renumbered 1-5
    // (Dashboard, Access, My Sessions, Auditors, Settings).
    {
      // F-078: NOT in the nav. The only tab a pure auditor could see here was
      // Audit Logs, which reads GET /api/audit-logs — a route declaring
      // portal: "app" that blocks auditors by design, so it 403s and renders
      // "No audit logs found". With that tab gone the page is empty for an
      // auditor, so it is no longer a destination. The route is kept (without
      // audit-logs) only for a multi-role user — an org admin who is ALSO an
      // auditor group admin — whom resolveHomePortal sends to the audit
      // portal and who would otherwise lose organization/facility settings.
      // Auditors reach their account via /audit/profile from the header menu.
      path: "settings",
      Component: SettingsPage,
      children: [
        { index: true, Component: SettingsIndexRedirect },
        { path: "organization", Component: OrganizationSettingsPage },
        { path: "facility", Component: FacilitySettingsPage },
        // US-177 (F-131): these two were REMOVED by the audit-portal IA change
        // and replaced by the `auditors` route. Nothing was left behind, so a
        // bookmark or an old link fell through to the global "*" catch-all in
        // routes.tsx and bounced to /login — telling the user nothing, and
        // indistinguishable from a permission refusal or a broken app.
        // A silent redirect is right where a path has a direct equivalent
        // (group-homes -> /audit/access?tab=..., above). These do not: the
        // replacement is a different page the user has to choose to enter.
        {
          path: "auditor-group",
          element: createElement(RouteMovedNotice, {
            what: "Auditor group settings",
            to: "/audit/auditors",
            toLabel: "Auditors",
          }),
        },
        {
          path: "invitation-requests",
          element: createElement(RouteMovedNotice, {
            what: "Invitation requests",
            to: "/audit/auditors",
            toLabel: "Auditors",
          }),
        },
      ],
    },
  ],
};
